import React, { useEffect, useState } from "react";
import { useSearchParams } from 'react-router-dom';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import UserFileService from "../../Services/UserFileService";
import UserFile from "../../Models/UserFile";
import { Filter, FilterType } from "../../Models/Filter";
import SearchBox from "../../Components/SearchBox/SearchBox";
import DropdownFilter from "../../Components/DropdownFilter/DropdownFilter";
import SuggestedFilters from "../../Components/SuggestedFilters/SuggestedFilters";
import ActiveFilters from "../../Components/ActiveFilters/ActiveFilters";
import TableActions from "../../Components/TableActions/TableActions";
import DocumentsTable from "../../Components/DocumentsTable/DocumentsTable";
import Pagination from "../../Components/Pagination/Pagination";
import "./MyFilesView.css";
import Loader from "../../Components/Loader/Loader";
import { error } from "console";
import TokenExpiredPopUp from "../../Components/TokenExpiredPopUp/TokenExpiredPopUp";

const MyFilesView: React.FC = () => {
    // Get User Token from Search Params
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [files, setFiles] = useState<UserFile[]>([]);
    const [filtredFiles, setFiltredFiles] = useState<UserFile[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<UserFile[]>([]);
    const [allFilters, setAllFilters] = useState<Filter[]>([]);
    const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [openfilters, setOpenfilters] = useState<boolean>(false);
    const [tokenExpired, setTokenExpired] = useState<boolean>(false);


    const [loading, setLoading] = useState(true);
    const PAGE_SIZE = 15;
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);

    // Get user Files (server-side pagination)
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            try {
                const data = await UserFileService.getAll(token);
                if (cancelled) return;
                setFiles(data);
                setFiltredFiles(data);
                setTotalItems(data.length);
            } catch (e: any) {
                if (!cancelled) {
                    setFiles([]);
                    setFiltredFiles([]);
                    setTotalItems(0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true };
    }, [token]);

    // Generate new filters when files change
    useEffect(() => {
        generateFilters(files);
    }, [files]);

    // Generate filters from data
    const generateFilters = (data: UserFile[]) => {
        const counts: Record<string, Filter> = {};

        for (const filter of Object.values(FilterType)) {
            for (const file of data) {
                let key: string;
                let value: string;
                let filterType: FilterType;

                switch (filter) {
                    case FilterType.IsRead:
                        value = file.isRead ? 'Lu' : 'Non Lu';
                        key = `${filter}:${value}`;
                        filterType = FilterType.IsRead;
                        break;

                    case FilterType.CreatedAt:
                        value = String(new Date(file.createdAt).getFullYear());
                        key = `${filter}:${value}`;
                        filterType = FilterType.CreatedAt;
                        break;

                    default:
                        value = String(file[filter]);
                        key = `${filter}:${value}`;
                        filterType = filter;
                        break;
                }

                if (!counts[key]) {
                    counts[key] = {
                        filterType,
                        value,
                        count: 0,
                    };
                }

                if (!file.isRead) {
                    counts[key].count += 1;
                }
            }
        }

        
        const customDocTypeOrder = [
            "Relevés",
            "Factures",
            "Contrats de Mandats",
            "Remboursements chèques virtualisés",
            "Remises fournisseurs",
            "Boni de gestion",
            "Confirmations SIDJ",
            "Confirmations SIDJ texte",
            "Expéditions SIDJ"
        ];

        const sorted = Object.values(counts).sort((a, b) => {
            if (a.filterType === FilterType.DocumentType && b.filterType === FilterType.DocumentType)
            {
                const indexA = customDocTypeOrder.indexOf(a.value);
                const indexB = customDocTypeOrder.indexOf(b.value);

                const aInOrder = indexA !== -1;
                const bInOrder = indexB !== -1;

                if (aInOrder && bInOrder) {
                    return indexA - indexB;
                } else if (aInOrder) {
                    return -1;
                } else if (bInOrder) {
                    return 1;
                } else {
                    return 0;
                }

            }
            return 0;
        });
        setAllFilters(sorted);
    };


    // Handel document search
    const handleSearch = (query: string) => {
        setSearchQuery(query);
        applyFilters(query, activeFilters);
    };

    // handle filter selection
    const toggleFilter = (filter: Filter) => {
        setActiveFilters((prev) => {
            const isActive = prev.some(f => f.filterType === filter.filterType && f.value === filter.value);

            const newFilters = isActive
                ? prev.filter(f => !(f.filterType === filter.filterType && f.value === filter.value))
                : [...prev, filter];

            applyFilters(searchQuery, newFilters);

            return newFilters;
        });
        setCurrentPage(1);
    };

    // Apply filter to files
    const applyFilters = (query: string, filters: Filter[]) => {
        const lowerQuery = query.toLowerCase();

        // Regrouper les filtres par type pour appliquer OR intra-type et AND inter-types
        const typeToValues = filters.reduce<Record<string, string[]>>((acc, f) => {
            if (!acc[f.filterType]) acc[f.filterType] = [];
            acc[f.filterType].push(f.value);
            return acc;
        }, {});

        const activeTypes = Object.keys(typeToValues);

        const filtered = files.filter(file => {
            const matchesSearch = file.name.toLowerCase().includes(lowerQuery);

            const matchesAllTypes = activeTypes.every(type => {
                const values = typeToValues[type];
                if (type === 'isRead') {
                    return values.some(v => (v === 'Lu' && file.isRead) || (v === 'Non Lu' && !file.isRead));
                }
                if (type === 'createdAt') {
                    const year = String(new Date(file.createdAt).getFullYear());
                    return values.includes(year);
                }
                const field = type as keyof UserFile;
                const fieldValue = file[field];
                if (fieldValue === undefined || fieldValue === null) return false;
                return values.some(v => String(fieldValue).toLowerCase() === v.toLowerCase());
            });

            return matchesSearch && (activeTypes.length === 0 ? true : matchesAllTypes);
        });

        setFiltredFiles(filtered);
        setTotalItems(filtered.length);
        setSelectedFiles(selectedFiles.filter((f) => filtered.includes(f)))
    };

    // Download selected documents
    const handleDocumentsDownload = async () => {
        if (!selectedFiles || selectedFiles.length === 0) {
            console.warn("No file selected.");
            return;
        }
        try {
            const blob = await UserFileService.downloadFiles(selectedFiles, token);
            
            if (!blob || blob.size === 0) {
                throw new Error("Le fichier téléchargé est vide.");
            }
            
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${selectedFiles.length === 1 ? selectedFiles[0].name : ("documents_" + new Date().toLocaleString() + ".zip")}`);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);

            setSelectedFiles(selectedFiles.map((file) => {
                file.isRead = true
                return file;
            }));
        } catch (e: any) {
            if (e.status === 401 && files.length !== 0) {
                setTokenExpired(true);
            }
            console.error("Erreur lors du téléchargement :", e);
            // Re-throw the error so TableActions can catch it and display an error message
            throw e;
        }
    }

    if (loading) return <Loader/>;

    if (files.length === 0) 
        return (<TokenExpiredPopUp/>);

    return (
        <div id="Document-View">
            { tokenExpired && (<TokenExpiredPopUp/>)}
            <div id="Header">
                <h3>Mes Documents</h3>
                {/* Documents search box */}
                <SearchBox onSearch={handleSearch} className="Seach-Box" searchQuery={searchQuery}/>
            </div>
            {/* Suggested Filters */}
            <SuggestedFilters allFilters={allFilters.filter(f => f.filterType === FilterType.DocumentType)} activeFilters={activeFilters.filter(f => f.filterType === FilterType.DocumentType)} onToggle={toggleFilter} />

            {/* Dropdown filters */}
            <button className="Show-Filter-Button" onClick={() => setOpenfilters(!openfilters)}>{openfilters ? "Masquer les filtres" : "Afficher les filtres"}<MdOutlineKeyboardArrowRight className={`Show-Filter-Button-Arrow ${openfilters ? "Open" : "Close"}`} /></button>
            <div id="Filters" className={openfilters ? "Open" : "Close"}>
                <DropdownFilter label="Magasins" onToggle={toggleFilter} filters={allFilters.filter((f) => f.filterType === 'storeName')} activeFilters={activeFilters.filter((f) => f.filterType === 'storeName')} />
                <DropdownFilter label="Types de document" onToggle={toggleFilter} filters={allFilters.filter((f) => f.filterType === 'documentType')} activeFilters={activeFilters.filter((f) => f.filterType === 'documentType')} className="Document-Type" />
                <DropdownFilter label="Entité" onToggle={toggleFilter} filters={allFilters.filter((f) => f.filterType === 'entityType')} activeFilters={activeFilters.filter((f) => f.filterType === 'entityType')} />
                <DropdownFilter label="Statut" onToggle={toggleFilter} filters={allFilters.filter((f) => f.filterType === 'isRead')} activeFilters={activeFilters.filter((f) => f.filterType === 'isRead')} />
                <DropdownFilter label="Année" onToggle={toggleFilter} filters={allFilters.filter((f) => f.filterType === 'createdAt')} activeFilters={activeFilters.filter((f) => f.filterType === 'createdAt')} />
            </div>

            {/* Active filters */}
            <ActiveFilters
                activeFilters={activeFilters}
                onToggle={toggleFilter}
                resetFilters={() => {
                    applyFilters(searchQuery, []);
                    setActiveFilters([]);
                }} />

            {filtredFiles.length > 0 ? <>
                {/* Main header */}
                <div id="Main-Header-Container">
                    {/* Number of documents */}
                    <div className="Documents-Number">
                        <h2 className="Documents-Number-Text">
                            {totalItems} documents
                        </h2>
                    </div>

                    {/* Table actions */}
                    <TableActions
                        selectedFiles={selectedFiles}
                        filtredFiles={filtredFiles}
                        files={files}
                        setFiles={setFiles}
                        handleDocumentsDownload={handleDocumentsDownload}
                        setTokenExpired={setTokenExpired}
                    />
                </div>

                {/* Selected files */}
                <DocumentsTable
                    files={files}
                    filtredFiles={filtredFiles}
                    selectedFiles={selectedFiles}
                    setFiles={setFiles}
                    setSelectedFiles={setSelectedFiles}
                    setFiltredFiles={setFiltredFiles}
                    setTokenExpired={setTokenExpired}
                    currentPage={currentPage}
                    pageSize={PAGE_SIZE}
                />
                <Pagination currentPage={currentPage} totalItems={totalItems} pageSize={PAGE_SIZE} onChange={(Pagenumber) => { setCurrentPage(Pagenumber); setSelectedFiles([]);} } />
            </> : <p>Aucun fichier n'a été trouvé, veuillez modifier votre recherche.</p>}
        </div>
    );
};

export default MyFilesView;
